/**
 * Line - Straight line segment between two points
 *
 * Rhino-style: Lines can reference Point objects via pointIds.
 * When the referenced points move, the line updates automatically.
 */

import { GeometryType, generateId } from '../base/GeometryBase';
import { Vector } from '../base/Vector';
import { CurveBase } from './CurveBase';

export class Line extends CurveBase {
  /**
   * Create a line
   * @param {Vector|{x,y,z}} start - Start point (or null if using pointIds)
   * @param {Vector|{x,y,z}} end - End point (or null if using pointIds)
   * @param {Object} attributes - Including optional pointIds: [startPointId, endPointId]
   */
  constructor(start, end, attributes = {}) {
    super(GeometryType.LINE, 'ln', attributes);

    // Extract pointIds from attributes
    const pointIds = attributes.pointIds || null;
    this._pointIds = pointIds;

    if (start && end) {
      this.start = start instanceof Vector ? start : Vector.fromObject(start);
      this.end = end instanceof Vector ? end : Vector.fromObject(end);
    } else {
      // Empty - will be filled from external point state
      this.start = new Vector(0, 0, 0);
      this.end = new Vector(0, 0, 0);
    }
  }

  /**
   * Get point IDs [startPointId, endPointId]
   */
  get pointIds() {
    return this._pointIds;
  }

  /**
   * Set point IDs
   */
  set pointIds(ids) {
    this._pointIds = ids;
    this.touch();
  }

  /**
   * Check if this line uses point references
   */
  get usesPointReferences() {
    return this._pointIds && this._pointIds.length === 2;
  }

  /**
   * Update internal vectors from external point state
   * @param {Function} getPoint - Function to get point by ID (returns {x, y, z})
   */
  updateFromPoints(getPoint) {
    if (!this.usesPointReferences) return;

    const startPt = getPoint(this._pointIds[0]);
    const endPt = getPoint(this._pointIds[1]);

    if (startPt) {
      this.start = new Vector(startPt.x, startPt.y, startPt.z || 0);
    }
    if (endPt) {
      this.end = new Vector(endPt.x, endPt.y, endPt.z || 0);
    }
  }

  /**
   * Get center point of line
   */
  get centerPoint() {
    return this.start.lerp(this.end, 0.5);
  }

  get length() {
    return this.start.distanceTo(this.end);
  }

  get direction() {
    return this.end.subtract(this.start).normalize();
  }

  pointAt(t) {
    return this.start.lerp(this.end, t);
  }

  tangentAt(t) {
    return this.direction;
  }

  get startPoint() {
    return this.start;
  }

  get endPoint() {
    return this.end;
  }

  closestPoint(point) {
    const v = this.end.subtract(this.start);
    const w = point.subtract(this.start);
    const c1 = w.dot(v);

    if (c1 <= 0) {
      return { point: this.start, parameter: 0, distance: point.distanceTo(this.start) };
    }

    const c2 = v.dot(v);
    if (c2 <= c1) {
      return { point: this.end, parameter: 1, distance: point.distanceTo(this.end) };
    }

    const t = c1 / c2;
    const closestPt = this.pointAt(t);
    return { point: closestPt, parameter: t, distance: point.distanceTo(closestPt) };
  }

  splitAt(t) {
    const midPt = this.pointAt(t);
    return [
      new Line(this.start, midPt, { ...this.attributes }),
      new Line(midPt, this.end, { ...this.attributes }),
    ];
  }

  /**
   * Extend line by distance
   * @param {number} startExtend - Distance to extend at start (negative = shrink)
   * @param {number} endExtend - Distance to extend at end
   * @returns {Line}
   */
  extend(startExtend = 0, endExtend = 0) {
    const dir = this.direction;
    const newStart = this.start.subtract(dir.multiply(startExtend));
    const newEnd = this.end.add(dir.multiply(endExtend));
    return new Line(newStart, newEnd, { ...this.attributes });
  }

  /**
   * Offset line perpendicular to direction
   * @param {number} distance - Offset distance (positive = left)
   * @returns {Line}
   */
  offset(distance) {
    const perp = new Vector(-this.direction.y, this.direction.x, 0).multiply(distance);
    return new Line(
      this.start.add(perp),
      this.end.add(perp),
      { ...this.attributes }
    );
  }

  /**
   * Get intersection with another line
   * @param {Line} other
   * @returns {{point: Vector, t1: number, t2: number}|null}
   */
  intersectLine(other) {
    const p1 = this.start;
    const p2 = this.end;
    const p3 = other.start;
    const p4 = other.end;

    const d1 = p2.subtract(p1);
    const d2 = p4.subtract(p3);
    const d3 = p1.subtract(p3);

    const cross = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(cross) < 1e-10) {
      return null; // Parallel lines
    }

    const t1 = (d2.x * d3.y - d2.y * d3.x) / cross;
    const t2 = (d1.x * d3.y - d1.y * d3.x) / cross;

    return {
      point: this.pointAt(t1),
      t1,
      t2,
    };
  }

  clone() {
    const line = new Line(this.start.clone(), this.end.clone(), { ...this.attributes });
    line._pointIds = this._pointIds ? [...this._pointIds] : null;
    return line;
  }

  transform({ translate, rotate, scale, mirror }) {
    let start = this.start.clone();
    let end = this.end.clone();

    if (scale) {
      const s = typeof scale === 'number' ? scale : 1;
      start = start.multiply(s);
      end = end.multiply(s);
    }

    if (rotate) {
      start = start.rotate2D(rotate);
      end = end.rotate2D(rotate);
    }

    if (translate) {
      const t = Vector.fromObject(translate);
      start = start.add(t);
      end = end.add(t);
    }

    if (mirror) {
      const n = Vector.fromObject(mirror);
      start = start.mirror(n);
      end = end.mirror(n);
    }

    return new Line(start, end, { ...this.attributes });
  }

  getBoundingBox() {
    return {
      min: {
        x: Math.min(this.start.x, this.end.x),
        y: Math.min(this.start.y, this.end.y),
        z: Math.min(this.start.z, this.end.z),
      },
      max: {
        x: Math.max(this.start.x, this.end.x),
        y: Math.max(this.start.y, this.end.y),
        z: Math.max(this.start.z, this.end.z),
      },
    };
  }

  getDisplayPoints(segments = 1) {
    return [this.start, this.end];
  }

  toJSON() {
    return {
      ...super.toJSON(),
      start: this.start.toObject(),
      end: this.end.toObject(),
      pointIds: this._pointIds || null,
    };
  }

  static fromJSON(json) {
    const line = new Line(
      Vector.fromObject(json.start),
      Vector.fromObject(json.end),
      { ...json.attributes, pointIds: json.pointIds || null }
    );
    line.id = json.id;
    line.createdAt = json.createdAt;
    line.modifiedAt = json.modifiedAt;
    return line;
  }
}

export default Line;
